import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import { telHref } from "../utils/format";
import SocialIcons from "./SocialIcons";

const Header = () => {
  const { config, navigation, content } = useSite();
  const { emergencyPopup } = content;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const c = config.contact;
  const isChildActive = (item) =>
    item.children.some(
      (ch) => pathname === ch.to || pathname.startsWith(`${ch.to}/`),
    );

  // Close menus on navigation
  useEffect(() => {
    setMenuOpen(false);
    setEmergencyOpen(false);
    setOpenDropdown(null);
  }, [pathname]);

  // Sticky navbar on desktop (replaces the old jQuery main.js behaviour)
  useEffect(() => {
    const onScroll = () =>
      setSticky(window.innerWidth >= 992 && window.scrollY > 150);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <header className="header header-layout1">
      <div className="header-topbar">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                <ul className="contact__list d-flex flex-wrap align-items-center list-unstyled mb-0">
                  <li className={emergencyOpen ? "active" : ""}>
                    <button
                      type="button"
                      className={`miniPopup-emergency-trigger ${emergencyOpen ? "active" : ""}`}
                      onClick={() => setEmergencyOpen((v) => !v)}
                      aria-expanded={emergencyOpen}
                    >
                      24/7 Emergency
                    </button>

                    <div
                      className={`miniPopup miniPopup-emergency text-center ${emergencyOpen ? "active" : ""}`}
                    >
                      <div className="emergency__icon">
                        <i className="icon-call3"></i>
                      </div>
                      <a
                        href={telHref(c.emergencyPhone)}
                        className="phone__number"
                      >
                        <i className="icon-phone"></i>
                        <span>{c.emergencyPhoneDisplay}</span>
                      </a>
                      <p>{emergencyPopup.desc}</p>
                      <Link
                        to="/appointment"
                        className="btn btn__secondary btn__link btn__block"
                      >
                        <span>Make Appointment</span>
                        <i className="icon-arrow-right"></i>
                      </Link>
                    </div>
                  </li>

                  <li>
                    <i className="icon-phone"></i>
                    <a href={telHref(c.phone)}>{c.phoneDisplay}</a>
                  </li>
                  <li>
                    <i className="icon-location"></i>
                    <a
                      href={config.map.directionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {c.address}
                    </a>
                  </li>
                  <li>
                    <i className="icon-clock"></i>
                    <Link to="/time-table">{config.hoursSummary}</Link>
                  </li>
                </ul>

                <div className="d-flex">
                  <SocialIcons
                    social={config.social}
                    className="social-icons list-unstyled mb-0 mr-30"
                  />

                  <form
                    className="header-topbar__search"
                    role="search"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (query.trim())
                        navigate(
                          `/search?q=${encodeURIComponent(query.trim())}`,
                        );
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      aria-label="Search the site"
                    />
                    <button
                      type="submit"
                      className="header-topbar__search-btn"
                      aria-label="Search"
                    >
                      <i className="fa fa-search"></i>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <nav
        className={`navbar navbar-expand-lg sticky-navbar ${sticky ? "is-sticky" : ""}`}
      >
        <div className="container-fluid">
          <Link className="navbar-brand" to="/">
            <img
              src={config.logo.light}
              className="logo-light"
              alt={config.name}
            />
            <img
              src={config.logo.dark}
              className="logo-dark"
              alt={config.name}
            />
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            <span className="menu-lines">
              <span></span>
            </span>
          </button>

          <div
            className={`collapse navbar-collapse ${menuOpen ? "menu-opened" : ""}`}
            id="mainNavigation"
          >
            <ul className="navbar-nav ml-auto">
              {navigation.map((item) =>
                item.children ? (
                  <li
                    key={item.label}
                    className={`nav__item has-dropdown ${openDropdown === item.label ? "opened" : ""}`}
                  >
                    <button
                      type="button"
                      className={`nav__item-link nav__dropdown-btn ${isChildActive(item) ? "active" : ""}`}
                      onClick={() =>
                        setOpenDropdown((v) =>
                          v === item.label ? null : item.label,
                        )
                      }
                      aria-haspopup="true"
                      aria-expanded={openDropdown === item.label}
                    >
                      {item.label} <i className="fas fa-angle-down"></i>
                    </button>
                    <ul
                      className={`dropdown-menu ${openDropdown === item.label ? "show" : ""}`}
                    >
                      {item.children.map((child) => (
                        <li key={child.to} className="nav__item">
                          <NavLink
                            to={child.to}
                            className={({ isActive }) =>
                              `nav__item-link ${isActive ? "active" : ""}`
                            }
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li
                    key={item.to}
                    className={`nav__item ${item.mobileOnly ? "d-lg-none d-md-block" : ""}`}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        `nav__item-link ${isActive ? "active" : ""}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ),
              )}
            </ul>

            <button
              onClick={() => setMenuOpen(false)}
              className="close-mobile-menu d-block d-lg-none"
              aria-label="Close menu"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="d-none d-xl-flex align-items-center position-relative ml-30">
            <Link
              to="/appointment"
              className="btn btn__primary btn__rounded ml-30"
            >
              <i className="icon-calendar"></i>
              <span>Appointment</span>
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
